import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, GripVertical, Eye, EyeOff, Image as ImageIcon, X, Save, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSlides() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSlide, setEditingSlide] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    description: '',
    image: '',
    cta_text: 'View Packages',
    cta_link: '/tours',
    is_active: true
  });

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchSlides();
  }, []);

  const fetchSlides = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/slides`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSlides(response.data);
    } catch (error) {
      console.error('Error fetching slides:', error);
      toast.error('Failed to load slides');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      subtitle: '',
      description: '',
      image: '',
      cta_text: 'View Packages',
      cta_link: '/tours',
      is_active: true
    });
    setEditingSlide(null);
    setShowForm(false);
  };

  const handleEdit = (slide) => {
    setFormData({
      title: slide.title || '',
      subtitle: slide.subtitle || '',
      description: slide.description || '',
      image: slide.image || '',
      cta_text: slide.cta_text || 'View Packages',
      cta_link: slide.cta_link || '/tours',
      is_active: slide.is_active !== false
    });
    setEditingSlide(slide);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.image) {
      toast.error('Title and Image URL are required');
      return;
    }

    setSaving(true);
    try {
      if (editingSlide) {
        await axios.put(
          `${API_URL}/api/admin/slides/${editingSlide.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Slide updated successfully');
      } else {
        await axios.post(
          `${API_URL}/api/admin/slides`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Slide created successfully');
      }
      resetForm();
      fetchSlides();
    } catch (error) {
      console.error('Error saving slide:', error);
      toast.error('Failed to save slide');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (slideId) => {
    if (!window.confirm('Are you sure you want to delete this slide?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/slides/${slideId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Slide deleted successfully');
      fetchSlides();
    } catch (error) {
      console.error('Error deleting slide:', error);
      toast.error('Failed to delete slide');
    }
  };

  const toggleActive = async (slide) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/slides/${slide.id}`,
        { is_active: !slide.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Slide ${slide.is_active ? 'hidden' : 'shown'}`);
      fetchSlides();
    } catch (error) {
      toast.error('Failed to update slide');
    }
  };

  const moveSlide = async (index, direction) => {
    const newSlides = [...slides];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= slides.length) return;
    
    [newSlides[index], newSlides[targetIndex]] = [newSlides[targetIndex], newSlides[index]];
    
    const slideOrders = newSlides.map((s, i) => ({ id: s.id, order: i }));
    
    try {
      await axios.put(
        `${API_URL}/api/admin/slides/reorder`,
        slideOrders,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSlides(newSlides);
      toast.success('Slide order updated');
    } catch (error) {
      toast.error('Failed to reorder slides');
      fetchSlides();
    }
  };

  const seedDefaults = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/slides/seed-defaults`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchSlides();
    } catch (error) {
      toast.error('Failed to seed default slides');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#FF6B35] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-slides-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#004E64]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Hero Slider Management
          </h1>
          <p className="text-[#4A4A4A] mt-1">
            Manage the homepage slider images. Add as many slides as you want.
          </p>
        </div>
        <div className="flex gap-2">
          {slides.length === 0 && (
            <Button variant="outline" onClick={seedDefaults} data-testid="seed-defaults-btn">
              Load Defaults
            </Button>
          )}
          <Button 
            onClick={() => setShowForm(true)} 
            className="bg-[#FF6B35] hover:bg-[#E85A2D]"
            data-testid="add-slide-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New Slide
          </Button>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#004E64]">
                {editingSlide ? 'Edit Slide' : 'Add New Slide'}
              </h2>
              <button onClick={resetForm} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#4A4A4A] mb-1">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Discover Paradise"
                    data-testid="slide-title-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#4A4A4A] mb-1">
                    Subtitle
                  </label>
                  <Input
                    value={formData.subtitle}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    placeholder="e.g., Explore the Maldives"
                    data-testid="slide-subtitle-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#4A4A4A] mb-1">
                  Description
                </label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Short description for the slide"
                  data-testid="slide-description-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#4A4A4A] mb-1">
                  Image URL <span className="text-red-500">*</span>
                </label>
                <Input
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                  data-testid="slide-image-input"
                />
                {formData.image && (
                  <div className="mt-2 relative aspect-video rounded-lg overflow-hidden bg-gray-100">
                    <img 
                      src={formData.image} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#4A4A4A] mb-1">
                    Button Text
                  </label>
                  <Input
                    value={formData.cta_text}
                    onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                    placeholder="View Packages"
                    data-testid="slide-cta-text-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#4A4A4A] mb-1">
                    Button Link
                  </label>
                  <Input
                    value={formData.cta_link}
                    onChange={(e) => setFormData({ ...formData, cta_link: e.target.value })}
                    placeholder="/tours"
                    data-testid="slide-cta-link-input"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-[#FF6B35] focus:ring-[#FF6B35]"
                  data-testid="slide-active-checkbox"
                />
                <label htmlFor="is_active" className="text-sm text-[#4A4A4A]">
                  Active (visible on homepage)
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-[#FF6B35] hover:bg-[#E85A2D]"
                  disabled={saving}
                  data-testid="save-slide-btn"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {editingSlide ? 'Update Slide' : 'Create Slide'}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Slides List */}
      {slides.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
          <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[#004E64] mb-2">No slides yet</h3>
          <p className="text-[#4A4A4A] mb-4">
            Add your first hero slider image or load default slides.
          </p>
          <div className="flex justify-center gap-2">
            <Button variant="outline" onClick={seedDefaults}>
              Load Defaults
            </Button>
            <Button onClick={() => setShowForm(true)} className="bg-[#FF6B35] hover:bg-[#E85A2D]">
              <Plus className="w-4 h-4 mr-2" />
              Add Slide
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {slides.map((slide, index) => (
            <div 
              key={slide.id} 
              className={`bg-white rounded-2xl border border-gray-100 overflow-hidden ${
                !slide.is_active ? 'opacity-60' : ''
              }`}
              data-testid={`slide-item-${slide.id}`}
            >
              <div className="flex flex-col md:flex-row">
                {/* Image Preview */}
                <div className="w-full md:w-64 h-40 md:h-auto bg-gray-100 flex-shrink-0">
                  <img 
                    src={slide.image} 
                    alt={slide.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/400x200?text=Image+Error';
                    }}
                  />
                </div>

                {/* Content */}
                <div className="flex-1 p-4 md:p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                          #{index + 1}
                        </span>
                        {!slide.is_active && (
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
                            Hidden
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-semibold text-[#004E64] truncate">
                        {slide.title}
                      </h3>
                      {slide.subtitle && (
                        <p className="text-[#FF6B35] text-sm font-medium">{slide.subtitle}</p>
                      )}
                      {slide.description && (
                        <p className="text-[#4A4A4A] text-sm mt-1 line-clamp-2">{slide.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span>Button: {slide.cta_text || 'None'}</span>
                        <span>Link: {slide.cta_link || '/'}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => moveSlide(index, 'up')}
                        disabled={index === 0}
                        className="p-2 text-gray-400 hover:text-[#004E64] hover:bg-gray-100 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Move up"
                      >
                        <ArrowUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => moveSlide(index, 'down')}
                        disabled={index === slides.length - 1}
                        className="p-2 text-gray-400 hover:text-[#004E64] hover:bg-gray-100 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Move down"
                      >
                        <ArrowDown className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => toggleActive(slide)}
                        className={`p-2 rounded-lg ${
                          slide.is_active 
                            ? 'text-green-600 hover:bg-green-50' 
                            : 'text-gray-400 hover:bg-gray-100'
                        }`}
                        title={slide.is_active ? 'Hide slide' : 'Show slide'}
                      >
                        {slide.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => handleEdit(slide)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                        title="Edit slide"
                        data-testid={`edit-slide-${slide.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(slide.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Delete slide"
                        data-testid={`delete-slide-${slide.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
        <h4 className="font-medium text-blue-800 mb-1">Tips for Great Slides</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Use high-quality images (recommended: 1920x1080 or higher)</li>
          <li>• Keep titles short and impactful</li>
          <li>• Use the reorder buttons to arrange slides in your preferred order</li>
          <li>• Toggle visibility to temporarily hide slides without deleting them</li>
        </ul>
      </div>
    </div>
  );
}
